import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { CheckCircle, Mail, Calendar, MapPin, Tag, Clock, AlertCircle } from "lucide-react";
import { AiAvatar, getCategorySuccessMessage, PENDING_REVIEW_MESSAGE, REGISTRATION_FAILED_MESSAGE } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";

const IDLE_TIMEOUT = 60; // shorter timeout on confirmation screen

const categoryNames = {
  disabled_blue_badge: "Disabled Blue Badge",
  adult_with_child: "Adult with Child in Vehicle",
  extra_wide: "Extra Wide Bay",
  nhs_staff: "NHS Staff",
  military: "Military / Ex-Military"
};

export default function KioskConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const registration = location.state?.registration;
  const requiresReview = location.state?.requiresReview;
  const error = location.state?.error;
  const incomingSubmissionId = location.state?.submissionId;
  const incomingOutcomeType = location.state?.outcomeType;
  const idleTimerRef = useRef(null);
  
  // Use incoming submissionId or generate one
  const submissionIdRef = useRef(incomingSubmissionId || `confirmation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Determine the final outcome state - SINGLE SOURCE OF TRUTH
  // Priority: explicit outcomeType > error > requiresReview > registration status
  const getOutcomeState = () => {
    // If explicit outcomeType was passed, use it
    if (incomingOutcomeType) return incomingOutcomeType;
    
    // Check for explicit error conditions
    if (error) return "error";
    if (registration?.status === "error") return "error";
    
    // Check for pending/review state
    if (requiresReview || registration?.status === "pending_review") return "pending";
    
    // Check for success state - must have registration_id from backend
    if (registration?.status === "active" || registration?.registration_id) return "success";
    
    // Default to error if no valid state
    return "error";
  };

  // LOCK the outcome state on first computation - never changes after
  const outcomeStateRef = useRef(null);
  if (outcomeStateRef.current === null) {
    outcomeStateRef.current = getOutcomeState();
    console.log("[KioskConfirmation] LOCKED outcome:", outcomeStateRef.current, "submissionId:", submissionIdRef.current);
  }
  const outcomeState = outcomeStateRef.current;

  // Idle detection - return to attract after short timeout
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  if (!registration) {
    navigate("/kiosk");
    return null;
  }

  // Get the appropriate AI message based on outcome state - SINGLE SOURCE OF TRUTH
  const getAiMessage = () => {
    switch (outcomeState) {
      case "error":
        return REGISTRATION_FAILED_MESSAGE;
      case "pending":
        return PENDING_REVIEW_MESSAGE;
      case "success":
      default:
        return getCategorySuccessMessage(registration.category);
    }
  };

  // Get the appropriate heading based on outcome state
  const getHeading = () => {
    switch (outcomeState) {
      case "error":
        return "Registration Failed";
      case "pending":
        return "Submitted for Review";
      case "success":
      default:
        return "Registration Complete!";
    }
  };

  // Get the appropriate subheading based on outcome state
  const getSubheading = () => {
    switch (outcomeState) {
      case "error":
        return error || "An error occurred during registration";
      case "pending":
        return "Your registration is pending manual verification";
      case "success":
      default:
        return "Your vehicle has been added to the whitelist";
    }
  };

  // Get the appropriate icon based on outcome state
  const getStatusIcon = () => {
    switch (outcomeState) {
      case "error":
        return <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />;
      case "pending":
        return <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" />;
      case "success":
      default:
        return <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Indefinite";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div className="kiosk-container flex flex-col min-h-screen p-6 md:p-12" data-testid="kiosk-confirmation">
      {/* Header */}
      <div className="flex items-center justify-center mb-8">
        <ParkWiseLogo size="lg" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-center animate-fade-in max-w-2xl">
          {/* AI Avatar with state-appropriate message - uses global speech lock */}
          <div className="mb-6">
            <AiAvatar 
              message={getAiMessage()} 
              size="large"
              autoSpeak={true}
              submissionId={submissionIdRef.current}
              outcomeType={outcomeState}
            />
          </div>

          {/* Status icon */}
          {getStatusIcon()}

          <h1 
            className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="confirmation-heading"
          >
            {getHeading()}
          </h1>

          <p className="text-xl text-slate-600 mb-8" data-testid="confirmation-subheading">
            {getSubheading()}
          </p>

          {/* Only show registration details for success or pending states */}
          {outcomeState !== "error" && (
            <>
              {/* Registration details card */}
              <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 md:p-8 text-left">
                {/* Vehicle registration prominently displayed */}
                <div className="text-center mb-6 pb-6 border-b-2 border-slate-100">
                  <p className="text-sm uppercase tracking-wider text-slate-500 mb-2">Vehicle Registration</p>
                  <p className="text-4xl font-bold font-mono tracking-wider text-slate-900">
                    {registration.vehicle_registration}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Tag className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-slate-500">Category</p>
                      <p className="font-medium text-slate-900">
                        {categoryNames[registration.category] || registration.category}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-slate-500">Site</p>
                      <p className="font-medium text-slate-900">{registration.site_id}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-slate-500">Registered</p>
                      <p className="font-medium text-slate-900">
                        {formatDate(registration.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-slate-500">Valid Until</p>
                      <p className="font-medium text-slate-900">
                        {formatDate(registration.expires_at)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Email confirmation notice - only for success */}
                {outcomeState === "success" && registration.email && (
                  <div className="mt-6 pt-6 border-t-2 border-slate-100 flex items-center gap-3 text-slate-600">
                    <Mail className="w-5 h-5 text-green-500" />
                    <p>A confirmation email has been sent to <strong>{registration.email}</strong></p>
                  </div>
                )}

                {/* Pending review notice */}
                {outcomeState === "pending" && (
                  <div className="mt-6 pt-6 border-t-2 border-slate-100 flex items-center gap-3 text-amber-700 bg-amber-50 p-3 rounded-lg">
                    <Clock className="w-5 h-5 text-amber-500" />
                    <p>Your registration will be reviewed by our team. You'll receive an email once approved.</p>
                  </div>
                )}
              </div>

              {/* Instructions - only for success */}
              {outcomeState === "success" && (
                <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-blue-800">
                    <strong>What happens next?</strong> Your vehicle registration is now on our whitelist. 
                    You can use designated parking spaces without any further kiosk interaction.
                  </p>
                </div>
              )}

              {/* Pending instructions */}
              {outcomeState === "pending" && (
                <div className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-amber-800">
                    <strong>What happens next?</strong> Our team will review your registration. 
                    You will receive an email notification once your registration has been approved.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Error instructions */}
          {outcomeState === "error" && (
            <div className="mt-8 p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-red-800">
                <strong>Need help?</strong> Please try again or speak to a member of staff at the customer service desk.
              </p>
            </div>
          )}

          {/* Done button */}
          <button
            onClick={() => navigate("/kiosk")}
            className="kiosk-btn-primary mt-8"
            data-testid="done-button"
          >
            {outcomeState === "error" ? "Try Again" : "Done - Return to Start"}
          </button>
        </div>
      </div>

      {/* Auto-return notice */}
      <div className="text-center text-slate-400 text-sm">
        <p>This screen will automatically return to start in 30 seconds</p>
      </div>
    </div>
  );
}
